import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import Portal from 'Components/portal'
import classNames from 'classnames'
import { stopPropagation } from 'Components/new/community-card/interact'
import { unstable_batchedUpdates } from 'react-dom'
import style from './dialog.module.scss'

const modalStack: {
  id: number
  zIndex: number
}[] = []

let modalDom: HTMLDivElement | null
let modalId = 0

const getModal = () => {
  if (!modalDom) {
    modalDom = document.createElement('div')
    modalDom.className = `${style['dcd-modal']}`
    document.body.appendChild(modalDom)
  }
  return modalDom
}

interface IModalProp {
  visible?: boolean
}

function useModal(props: IModalProp) {
  const { visible } = props
  const zIndexRef = useRef(
    modalStack[modalStack.length - 1]?.zIndex + 2 || 10001
  )

  useEffect(() => {
    if (!visible) return
    const _modalDom = getModal()
    _modalDom.style.zIndex = String(zIndexRef.current)
    const id = modalId++
    modalStack.push({ id, zIndex: zIndexRef.current })

    document.body.style.overflow = 'hidden'

    return () => {
      const modalIndex = modalStack.findIndex((d) => d.id === id)
      if (modalIndex > -1) {
        modalStack.splice(modalIndex, 1)
      }
      if (modalStack.length) {
        _modalDom.style.zIndex = String(
          modalStack[modalStack.length - 1].zIndex
        )
      } else {
        // _modalDom.onanimationend = () => {
        //   (_modalDom.parentNode || _modalDom.parentElement)!.removeChild(_modalDom)
        //   modalDom = null
        //   document.body.style.overflow = ''
        // }
        setTimeout(() => {
          ;(_modalDom.parentNode || _modalDom.parentElement)?.removeChild(
            _modalDom
          )
          modalDom = null
          document.body.style.overflow = ''
        }, 300)
        _modalDom.classList.add('leave')
      }
    }
  }, [visible])

  useEffect(() => {
    return () => {
      if (!modalStack.length) {
        // eslint-disable-next-line no-unused-expressions
        modalDom?.parentNode?.removeChild(modalDom)
        modalDom = null
      }
    }
  }, [])

  return zIndexRef.current + 1
}

export interface IDialogProps {
  className?: string
  headerClass?: string
  titleClass?: string
  headerVisible?: boolean
  visible?: boolean
  onClose?(): void
  closeText?: string
  title?: ReactNode
  destroyOnClose?: boolean
  onCloseBtnClick?(): void
}

const Dialog: React.FC<IDialogProps> = ({
  className,
  headerClass,
  titleClass,
  headerVisible = true,
  visible,
  onClose,
  closeText,
  title,
  children,
  destroyOnClose,
  onCloseBtnClick,
}) => {
  const [show, setShow] = useState(visible)
  const [isInit, setIsInit] = useState(!visible)
  const [isLeave, setIsLeave] = useState(false)

  useEffect(() => {
    unstable_batchedUpdates(() => {
      if (visible !== show) {
        setIsInit(false)
        if (visible) {
          setShow(visible)
        } else {
          setIsLeave(true)
        }
      }
    })
  }, [visible, show])

  const handleClose = useCallback(() => {
    setIsLeave(true)
  }, [])

  const handleAnimationEnd = useCallback(() => {
    if (isLeave) {
      if (onClose) onClose()
      setShow(false)
      setIsLeave(false)
    }
  }, [isLeave, onClose])

  const handleClickContent = stopPropagation()
  const handleMouseOver = stopPropagation()

  const zIndex = useModal({ visible: show })

  return isInit ? null : (
    <React.Fragment>
      <Portal getContainer={() => document.body}>
        <div
          className={style['dialog-wrapper']}
          style={{ display: show ? undefined : 'none', zIndex }}
          onClick={handleClose}
          onMouseOver={handleMouseOver}
        >
          <div
            className={classNames(
              style['dialog-content'],
              {
                leave: isLeave,
              },
              className
            )}
            onAnimationEnd={handleAnimationEnd}
            onClick={handleClickContent}
          >
            {headerVisible ? (
              <div className={classNames(headerClass, style['dialog-header'])}>
                <p
                  className={classNames(
                    style['dialog-title'],
                    'line-1',
                    titleClass
                  )}
                >
                  {title || ''}
                </p>
                <button
                  type={'button'}
                  className={style['dialog-close-btn']}
                  onClick={() => {
                    // eslint-disable-next-line no-unused-expressions
                    onCloseBtnClick?.()
                    handleClose()
                  }}
                >
                  <i
                    className={classNames('DCD_Icon icon_close_24', {
                      'tw-mr-2': closeText,
                    })}
                  />
                  {closeText}
                </button>
              </div>
            ) : null}
            {!show && destroyOnClose ? null : children}
          </div>
        </div>
      </Portal>
    </React.Fragment>
  )
}

export default Dialog
